import { SpecialtyTypeEnum } from "@app/projects/enumerators";
import { MultiMapExportRouteState, MultiMapExportSpecialty } from "@app/projects/network-assessment/multi-map-export/interfaces";

/** Required first, per AC 2. Never includes All or Unknown. */
export const SPECIALTY_TYPE_DISPLAY_ORDER: readonly SpecialtyTypeEnum[] = [
  SpecialtyTypeEnum.Required,
  SpecialtyTypeEnum.Informational,
];

/** The pill control reports selections as plain strings in click order. */
export const orderSpecialtyTypes = (types: readonly string[]): SpecialtyTypeEnum[] =>
  SPECIALTY_TYPE_DISPLAY_ORDER.filter((type) => types.includes(type));

export const availableSpecialtyTypes = (specialties: readonly MultiMapExportSpecialty[]): SpecialtyTypeEnum[] => {
  const present = new Set(specialties.map((specialty) => specialty.specialtyType));

  return SPECIALTY_TYPE_DISPLAY_ORDER.filter((type) => present.has(type));
};

export const specialtiesInScope = (
  specialties: readonly MultiMapExportSpecialty[],
  selectedTypes: readonly SpecialtyTypeEnum[],
): MultiMapExportSpecialty[] => specialties.filter((specialty) => selectedTypes.includes(specialty.specialtyType));

/**
 * Specialties that leave scope are dropped, specialties newly in scope arrive selected, and
 * specialties the user unchecked while in scope stay unchecked.
 */
export const reconcileSpecialtySelection = (
  specialties: readonly MultiMapExportSpecialty[],
  selectedSpecialtyGroupCodes: readonly string[],
  previousTypes: readonly SpecialtyTypeEnum[],
  nextTypes: readonly SpecialtyTypeEnum[],
): string[] => {
  const previouslyInScope = new Set(specialtiesInScope(specialties, previousTypes).map((specialty) => specialty.code));
  const selected = new Set(selectedSpecialtyGroupCodes);

  return specialtiesInScope(specialties, nextTypes)
    .filter((specialty) => selected.has(specialty.code) || !previouslyInScope.has(specialty.code))
    .map((specialty) => specialty.code);
};

/** "17 specialties" — what is in view, not what is selected. */
export const formatSpecialtySummary = (specialties: readonly MultiMapExportSpecialty[]): string =>
  `${specialties.length} ${specialties.length === 1 ? "specialty" : "specialties"}`;

export const formatSpecialtyLabel = (specialty: MultiMapExportSpecialty): string => `${specialty.code} - ${specialty.name}`;

export interface SpecialtySelection {
  selectedSpecialtyGroupCodes: string[];
  selectedSpecialtyTypes: SpecialtyTypeEnum[];
}

/**
 * The specialty the user came from on the Network Coverage Map becomes the selection. An absent or
 * unrecognised launch value means "arrived without this", never "nothing selected", so it falls
 * back to Select All. Every type present starts selected — the launch context carries no type.
 */
export const initialSpecialtySelection = (
  specialties: readonly MultiMapExportSpecialty[],
  launchContext?: MultiMapExportRouteState,
): SpecialtySelection => {
  const selectedSpecialtyTypes = availableSpecialtyTypes(specialties);
  const launchSpecialty = launchContext?.selectedSpecialtyGroupCode
    ? specialties.find((specialty) => specialty.code === launchContext.selectedSpecialtyGroupCode)
    : undefined;

  return {
    selectedSpecialtyGroupCodes: launchSpecialty
      ? [launchSpecialty.code]
      : specialtiesInScope(specialties, selectedSpecialtyTypes).map((specialty) => specialty.code),
    selectedSpecialtyTypes,
  };
};
